import { useEffect, useState } from "react";
import { toast } from "sonner";
import axios from "axios";
import { 
  Users, 
  ShoppingBag, 
  MessageSquare, 
  Bell,
  TrendingUp,
  ClipboardList,
  CheckCircle,
  Clock,
  Send,
  Trash2,
  ArrowRight,
  Ban,
  ChevronDown,
  Layout
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useNavigate } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TEMPLATES = [
  { id: 'betboom', name: 'Betboom' },
  { id: 'cashinout', name: 'Cashinout.io' },
  { id: 'onesix', name: 'ONESIX' },
  { id: 'bybit', name: 'P2P Биржа' },
  { id: 'ligastavok', name: 'Лига Ставок' },
  { id: 'platipomiru', name: 'Плати по миру' },
  { id: 'coinpay', name: 'CoinPay' },
  { id: 'cryptotrade', name: 'CryptoTrade' },
  { id: 'bitexchange', name: 'BitExchange' },
  { id: 'vulkanbet', name: 'VulkanBet' },
  { id: 'joycasino', name: 'JoyCasino' },
  { id: '1xstavka', name: '1xStavka' }
];

export default function Dashboard() {
  const [stats, setStats] = useState({
    users: 0,
    ozon_profiles: 0,
    messages: 0,
    notifications: 0,
    unlocked_ozon_count: 0,
    unlocked_ozon_sum: 0
  });
  const [requests, setRequests] = useState([]);
  const [requestsStats, setRequestsStats] = useState({
    total: 0,
    pending: 0,
    sent: 0,
    unlocked: 0,
    blocked: 0,
    pending_balance: 0,
    sent_balance: 0,
    unlocked_balance: 0,
    blocked_balance: 0
  });
  const [templateDropdown, setTemplateDropdown] = useState(null);
  const navigate = useNavigate();

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return { Authorization: `Bearer ${token}` };
  };

  const fetchStats = async () => {
    try {
      const results = await Promise.allSettled([
        axios.get(`${API}/admin/users`, { headers: getAuthHeaders() }),
        axios.get(`${API}/ozon/profiles`, { headers: getAuthHeaders() }),
        axios.get(`${API}/chat/messages`, { headers: getAuthHeaders() }),
        axios.get(`${API}/notifications`, { headers: getAuthHeaders() }),
        axios.get(`${API}/requests/stats`, { headers: getAuthHeaders() })
      ]);
      
      const usersData = results[0].status === 'fulfilled' ? results[0].value.data : [];
      const profilesData = results[1].status === 'fulfilled' ? results[1].value.data : [];
      const messagesData = results[2].status === 'fulfilled' ? results[2].value.data : [];
      const notificationsData = results[3].status === 'fulfilled' ? results[3].value.data : [];
      const requestsStatsData = results[4].status === 'fulfilled' ? results[4].value.data : {};
      
      // Count unlocked OZON profiles (is_blocked=false) from OZON Finance
      const activeProfiles = Array.isArray(profilesData) 
        ? profilesData.filter(p => !p.is_blocked) 
        : [];
      const unlockedCount = activeProfiles.length;
      const unlockedSum = activeProfiles.reduce((sum, p) => sum + (p.balance || 0), 0);
      
      setStats({
        users: Array.isArray(usersData) ? usersData.length : 0,
        ozon_profiles: Array.isArray(profilesData) ? profilesData.length : 0,
        messages: Array.isArray(messagesData) ? messagesData.length : 0,
        notifications: Array.isArray(notificationsData) ? notificationsData.length : 0,
        unlocked_ozon_count: unlockedCount,
        unlocked_ozon_sum: unlockedSum
      });
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const fetchRequests = async () => {
    try {
      const [requestsRes, statsRes] = await Promise.all([
        axios.get(`${API}/requests`, { headers: getAuthHeaders() }),
        axios.get(`${API}/requests/stats`, { headers: getAuthHeaders() })
      ]);
      setRequests(requestsRes.data || []);
      setRequestsStats({
        ...statsRes.data,
        unlocked: statsRes.data?.completed || 0,
        unlocked_balance: statsRes.data?.completed_balance || 0,
        blocked: statsRes.data?.blocked || 0,
        blocked_balance: statsRes.data?.blocked_balance || 0
      });
    } catch (error) {
      console.error("Error fetching requests:", error);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchRequests();
  }, []);

  const updateRequestStatus = async (requestId, status, template = null) => {
    try {
      const updateData = { status };
      if (template) {
        updateData.template = template;
      }
      await axios.put(
        `${API}/requests/${requestId}`,
        updateData,
        { headers: getAuthHeaders() }
      );
      const statusLabel = status === 'sent' ? 'Отправлено' : status === 'completed' ? 'Разблокирован' : status === 'blocked' ? 'Заблокирован' : status;
      const templateName = template ? TEMPLATES.find(t => t.id === template)?.name : '';
      toast.success(`Статус заявки изменён на "${statusLabel}"${templateName ? ` (${templateName})` : ''}`);
      setTemplateDropdown(null);
      fetchRequests();
    } catch (error) {
      toast.error("Ошибка обновления статуса");
    }
  };

  const deleteRequest = async (requestId) => {
    if (!window.confirm("Удалить заявку?")) return;
    try {
      await axios.delete(`${API}/requests/${requestId}`, { headers: getAuthHeaders() });
      toast.success("Заявка удалена");
      fetchRequests();
    } catch (error) {
      toast.error("Ошибка удаления заявки");
    }
  };

  const pendingRequests = requests.filter(r => r.status === 'pending');
  const sentRequests = requests.filter(r => r.status === 'sent');

  return (
    <div className="animate-fade-in space-y-6" data-testid="dashboard-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
          CRM Cash
        </h1>
        <p className="text-slate-500">Обзор системы и статистика</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Combined Unlocked Card */}
        <Card className="bg-gradient-to-br from-emerald-50 to-blue-50 border-emerald-200 lg:col-span-2">
          <CardContent className="p-4">
            <div className="flex items-center gap-6">
              <div className="p-3 bg-gradient-to-br from-emerald-500 to-blue-500 rounded-xl">
                <CheckCircle className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1 grid grid-cols-2 gap-4">
                <div className="border-r border-emerald-200 pr-4">
                  <p className="text-sm text-emerald-600 font-medium">Разблокированные OZON</p>
                  <p className="text-3xl font-bold text-emerald-700">{stats.unlocked_ozon_count}</p>
                  <p className="text-sm text-blue-600 font-semibold">{(stats.unlocked_ozon_count * 2000).toLocaleString()} ₽</p>
                </div>
                <div>
                  <p className="text-sm text-blue-600 font-medium">Разблокированный баланс</p>
                  <p className="text-3xl font-bold text-blue-700">{stats.unlocked_ozon_sum?.toLocaleString() || 0}</p>
                  <p className="text-sm text-emerald-600">₽ из активных профилей</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-green-50 border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500 rounded-lg">
                <ShoppingBag className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm text-green-600">OZON профили</p>
                <p className="text-2xl font-bold text-green-800">{stats.ozon_profiles}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-red-50 border-red-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-500 rounded-lg">
                <Ban className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm text-red-600">Заблокированные</p>
                <p className="text-2xl font-bold text-red-800">{requestsStats.blocked || 0}</p>
                <p className="text-xs text-red-500">Красный статус</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Requests Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="bg-yellow-50 border-yellow-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-500 rounded-lg">
                <Clock className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm text-yellow-600">Не отправленные заявки</p>
                <p className="text-2xl font-bold text-yellow-800">{requestsStats.pending}</p>
                <p className="text-sm text-yellow-600">{requestsStats.pending_balance?.toLocaleString()} ₽</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-cyan-50 border-cyan-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-cyan-500 rounded-lg">
                <Send className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm text-cyan-600">Отправленные заявки</p>
                <p className="text-2xl font-bold text-cyan-800">{requestsStats.sent}</p>
                <p className="text-sm text-cyan-600">{requestsStats.sent_balance?.toLocaleString()} ₽</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pending Requests */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Clock className="w-5 h-5 text-yellow-500" />
            Не отправленные заявки ({pendingRequests.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {pendingRequests.length === 0 ? (
            <div className="text-center py-4 text-slate-400">
              Нет не отправленных заявок
            </div>
          ) : (
            <div className="space-y-3">
              {pendingRequests.map((request) => (
                <div 
                  key={request.id} 
                  className="flex items-center justify-between p-3 bg-yellow-50 border border-yellow-200 rounded-lg"
                >
                  <div className="flex items-center gap-4">
                    <div>
                      <p className="font-medium text-slate-800">{request.profile_name}</p>
                      <p className="text-sm text-slate-500">
                        {request.profile_phone && <span className="mr-2">{request.profile_phone}</span>}
                        {new Date(request.created_at).toLocaleString('ru-RU')}
                      </p>
                    </div>
                    <Badge variant="outline" className="bg-yellow-100 text-yellow-700 border-yellow-300">
                      {request.balance?.toLocaleString()} ₽
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="relative">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setTemplateDropdown(templateDropdown === request.id ? null : request.id)}
                        className="text-cyan-600 border-cyan-300 hover:bg-cyan-50"
                      >
                        <Layout className="w-4 h-4 mr-1" />
                        Отправить
                        <ChevronDown className="w-3 h-3 ml-1" />
                      </Button>
                      {templateDropdown === request.id && (
                        <div className="absolute z-50 top-full mt-1 right-0 w-48 bg-white border border-slate-200 rounded-lg shadow-xl py-1">
                          <div className="px-3 py-2 text-xs font-semibold text-slate-500 border-b">Выберите шаблон</div>
                          {TEMPLATES.map((template) => (
                            <button
                              key={template.id}
                              onClick={() => updateRequestStatus(request.id, 'sent', template.id)}
                              className="w-full px-3 py-2 text-left text-sm hover:bg-cyan-50 text-slate-700 hover:text-cyan-700"
                            >
                              {template.name}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => deleteRequest(request.id)}
                      className="text-red-500 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Sent Requests */}
      {sentRequests.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Send className="w-5 h-5 text-cyan-500" />
              Отправленные заявки ({sentRequests.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {sentRequests.map((request) => (
                <div 
                  key={request.id} 
                  className="flex items-center justify-between p-3 bg-cyan-50 border border-cyan-200 rounded-lg"
                >
                  <div className="flex items-center gap-4">
                    <div>
                      <p className="font-medium text-slate-800">{request.profile_name}</p>
                      <p className="text-sm text-slate-500">
                        {request.profile_phone && <span className="mr-2">{request.profile_phone}</span>}
                        Отправлено: {request.sent_at ? new Date(request.sent_at).toLocaleString('ru-RU') : '—'}
                      </p>
                    </div>
                    <Badge variant="outline" className="bg-cyan-100 text-cyan-700 border-cyan-300">
                      {request.balance?.toLocaleString()} ₽
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => updateRequestStatus(request.id, 'completed')}
                      className="text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                    >
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Разблокирован
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => updateRequestStatus(request.id, 'blocked')}
                      className="text-red-600 border-red-300 hover:bg-red-50"
                    >
                      <Ban className="w-4 h-4 mr-1" />
                      Блок
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => deleteRequest(request.id)}
                      className="text-red-500 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Actions */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-slate-500" />
            Быстрые действия
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Button
              variant="outline"
              className="h-auto py-4 flex flex-col items-center gap-2 hover:bg-blue-50 hover:border-blue-300"
              onClick={() => navigate('/ozon')}
            >
              <ShoppingBag className="w-6 h-6 text-blue-500" />
              <span>Профили OZON</span>
            </Button>
            <Button
              variant="outline"
              className="h-auto py-4 flex flex-col items-center gap-2 hover:bg-purple-50 hover:border-purple-300"
              onClick={() => navigate('/chat')}
            >
              <MessageSquare className="w-6 h-6 text-purple-500" />
              <span>Открыть чат</span>
            </Button>
            <Button
              variant="outline"
              className="h-auto py-4 flex flex-col items-center gap-2 hover:bg-green-50 hover:border-green-300"
              onClick={() => navigate('/admin/users')}
            >
              <Users className="w-6 h-6 text-green-500" />
              <span>Управление пользователями</span>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
